import dayjs from 'dayjs'
import { BellIcon, ListIcon } from 'lucide-react'

import { Card, CardContent } from '@/components/card/card'
import { Separator } from '@/components/separator/separator'

import {
  type ClientPortalEoirCaseHistoryItem,
  type ClientPortalEoirCurrentStatus,
} from '@/lib/queries/case-dashboard/types'
import { cn } from '@/lib/utils'

export interface CaseHistoryCardProps {
  currentStatus?: ClientPortalEoirCurrentStatus
  historyData?: ClientPortalEoirCaseHistoryItem[]
}

export const CaseHistoryCard = ({ currentStatus, historyData = [] }: CaseHistoryCardProps) => {
  const formattedLastUpdateDate = currentStatus?.date ? dayjs.utc(currentStatus?.date).format('D MMM YYYY') : '-'

  return (
    <Card className={cn('lg:col-span-2')}>
      <CardContent className={cn('flex flex-col lg:flex-row', 'p-6')}>
        <div className={cn('w-full lg:max-w-[360px]')}>
          <p className={cn('flex items-center gap-x-2', 'text-lg font-semibold text-foreground')}>
            <BellIcon size={16} />
            Current Case Status
          </p>
          <p className={cn('text-[14px] leading-[24px] text-[#555]', 'mt-1.5')}>
            Last Updated on {formattedLastUpdateDate}.
            <br />
            Our system checks case tracking sites 20+ times a day.
          </p>
          <div className={cn('flex flex-col justify-center', 'mt-6', 'px-8 py-4', 'bg-blue-50', 'rounded-[10px]')}>
            <p className={cn('text-center', 'text-[18px] font-bold leading-[30px] text-foreground')}>
              {currentStatus?.status ?? '-'}
            </p>
            <p className={cn('text-[12px] font-normal leading-[18px] text-muted-foreground', 'mt-1.5', 'text-center')}>
              {formattedLastUpdateDate}
            </p>
          </div>
        </div>
        <Separator orientation="horizontal" className={cn('my-6', 'bg-elevated', 'block lg:hidden')} />
        <Separator orientation="vertical" className={cn('mx-6', 'bg-elevated', 'hidden lg:block')} />
        <div className={cn('flex-1')}>
          <p className={cn('flex items-center gap-x-2', 'text-lg font-semibold text-foreground')}>
            <ListIcon size={16} />
            History
          </p>
          <div className={cn('relative', 'mt-[50px]')}>
            {historyData.length > 1 && <VerticalGrayLine />}
            {/* history list */}
            <ol className={cn('flex flex-col gap-y-4', 'pl-4')}>
              {historyData.map(({ status, date }, index) => (
                <li
                  key={index}
                  className={cn(
                    'relative',
                    'flex flex-col gap-y-2 lg:flex-row lg:justify-between',
                    'p-4',
                    'rounded-[10px] bg-secondary lg:rounded-none lg:bg-transparent'
                  )}
                >
                  {/* blue dot on the left */}
                  <HistoryItemBlueDot />
                  {/* to mask the tips of vertical gray line at the top and the bottom */}
                  <WhiteMask isFirst={index === 0} isLast={index === historyData.length - 1} />
                  <p className={cn('lg:flex-1', 'lg:max-w-[480px]', 'text-lg font-medium text-foreground')}>{status}</p>
                  <p className={cn('lg:ml-3 lg:w-[85px]', 'text-left text-[14px] font-normal text-muted-foreground')}>
                    {dayjs.utc(date).format('D MMM YYYY')}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const VerticalGrayLine = () => {
  return <div className={cn('absolute left-[8px] top-0', 'w-[1px]', 'h-full', 'bg-border')} />
}

const HistoryItemBlueDot = () => {
  return (
    <span
      className={cn(
        'absolute left-[-12px] top-[50%] translate-y-[-50%]',
        'inline-block',
        'w-2',
        'h-2',
        'rounded-[50%]',
        'bg-blue-600'
      )}
    />
  )
}

const WhiteMask = ({ isFirst, isLast }: { isFirst: boolean; isLast: boolean }) => {
  return (
    <span
      className={cn(
        'hidden',
        {
          block: isFirst || isLast,
          'top-0': isFirst,
          'bottom-0': isLast,
        },
        'absolute left-[-8px]',
        'w-[1px]',
        'h-[calc(50%-4px)]',
        'bg-white'
      )}
    />
  )
}
